
import { useState, FormEvent } from 'react';

interface FormProps {
  language: 'pt' | 'en';
  isOpen: boolean;
  onClose: () => void;
}

export default function Form({ language, isOpen, onClose }: FormProps) {
  const [formData, setFormData] = useState({
    name: '',
    whatsapp: '',
    email: '',
    objective: '',
    assetValue: '',
    monthlyPayment: '',
    timeframe: '',
    hasConsortium: '',
    hasAwardedLetter: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const content = {
    pt: {
      title: 'Agendar Diagnóstico',
      name: 'Nome completo',
      whatsapp: 'WhatsApp',
      email: 'E-mail',
      objective: 'Objetivo principal',
      objectiveOptions: [
        { value: '', label: 'Selecione uma opção' },
        { value: 'Aquisição imobiliária', label: 'Aquisição imobiliária' },
        { value: 'Construção/Reforma', label: 'Construção/Reforma' },
        { value: 'Planejamento patrimonial', label: 'Planejamento patrimonial' },
        { value: 'Estruturação empresarial', label: 'Estruturação empresarial' },
        { value: 'Carta contemplada', label: 'Carta contemplada' }
      ],
      assetValue: 'Valor do ativo',
      monthlyPayment: 'Parcela mensal confortável',
      timeframe: 'Horizonte',
      hasConsortium: 'Já participa de consórcio?',
      hasAwardedLetter: 'Possui carta contemplada?',
      yes: 'Sim',
      no: 'Não',
      submit: 'Agendar Diagnóstico',
      successMessage: 'Solicitação enviada. Nossa equipe retornará em breve.',
      lgpdText: 'Ao preencher este formulário, você consente com o uso dos dados fornecidos pela FGT Capital exclusivamente para fins de diagnóstico operacional em consórcio, conforme previsto na Lei Geral de Proteção de Dados (LGPD - Lei nº 13.709/2018). Seus dados serão tratados com confidencialidade e não serão compartilhados com terceiros sem sua autorização.'
    },
    en: {
      title: 'Schedule Assessment',
      name: 'Full name',
      whatsapp: 'WhatsApp',
      email: 'E-mail',
      objective: 'Primary objective',
      objectiveOptions: [
        { value: '', label: 'Select an option' },
        { value: 'Real estate acquisition', label: 'Real estate acquisition' },
        { value: 'Construction/Renovation', label: 'Construction/Renovation' },
        { value: 'Wealth planning', label: 'Wealth planning' },
        { value: 'Corporate structuring', label: 'Corporate structuring' },
        { value: 'Awarded letter', label: 'Awarded letter' }
      ],
      assetValue: 'Asset value',
      monthlyPayment: 'Comfortable monthly payment',
      timeframe: 'Timeframe',
      hasConsortium: 'Already participating in consortium?',
      hasAwardedLetter: 'Have awarded letter?',
      yes: 'Yes',
      no: 'No',
      submit: 'Schedule Assessment',
      successMessage: 'Request submitted. Our team will contact you shortly.',
      lgpdText: 'By filling out this form, you consent to the use of the data provided by FGT Capital exclusively for operational consortium assessment purposes, as provided for in the General Data Protection Law (LGPD - Law No. 13,709/2018). Your data will be treated confidentially and will not be shared with third parties without your authorization.'
    }
  };

  const t = content[language];

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('whatsapp', formData.whatsapp);
      formBody.append('email', formData.email);
      formBody.append('objective', formData.objective);
      formBody.append('assetValue', formData.assetValue);
      formBody.append('monthlyPayment', formData.monthlyPayment);
      formBody.append('timeframe', formData.timeframe);
      formBody.append('hasConsortium', formData.hasConsortium);
      formBody.append('hasAwardedLetter', formData.hasAwardedLetter);

      const response = await fetch('https://readdy.ai/api/form/d6je57fgl4s4nd8dlsu0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitMessage(t.successMessage);
        setFormData({
          name: '',
          whatsapp: '',
          email: '',
          objective: '',
          assetValue: '',
          monthlyPayment: '',
          timeframe: '',
          hasConsortium: '',
          hasAwardedLetter: ''
        });
        setTimeout(() => {
          onClose();
          setSubmitMessage('');
        }, 2000);
      }
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 z-50 transition-opacity"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
        <div className="bg-[#FBF8F3] max-w-3xl w-full max-h-[90vh] overflow-y-auto pointer-events-auto border border-[#E7E1D9] shadow-2xl">
          <div className="sticky top-0 bg-[#FBF8F3] border-b border-[#E7E1D9] px-8 py-6 flex items-center justify-between z-10">
            <h2 className="text-2xl font-semibold text-[#0B0B0B]">
              {t.title}
            </h2>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center text-[#2E2E2E] hover:text-[#F97700] transition-colors cursor-pointer"
              aria-label="Close"
            >
              <i className="ri-close-line text-2xl"></i>
            </button>
          </div>

          <div className="px-8 py-8">
            <form onSubmit={handleSubmit} className="space-y-6" data-readdy-form>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm text-[#2E2E2E] mb-2 tracking-wide">
                    {t.name}
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="w-full px-4 py-3 bg-white border border-[#E7E1D9] text-[#0B0B0B] focus:outline-none focus:border-[#F97700] transition-colors"
                  />
                </div>

                <div>
                  <label className="block text-sm text-[#2E2E2E] mb-2 tracking-wide">
                    {t.whatsapp}
                  </label>
                  <input
                    type="tel"
                    name="whatsapp"
                    value={formData.whatsapp}
                    onChange={(e) => setFormData({ ...formData, whatsapp: e.target.value })}
                    required
                    className="w-full px-4 py-3 bg-white border border-[#E7E1D9] text-[#0B0B0B] focus:outline-none focus:border-[#F97700] transition-colors"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm text-[#2E2E2E] mb-2 tracking-wide">
                  {t.email}
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="w-full px-4 py-3 bg-white border border-[#E7E1D9] text-[#0B0B0B] focus:outline-none focus:border-[#F97700] transition-colors"
                />
              </div>

              <div>
                <label className="block text-sm text-[#2E2E2E] mb-2 tracking-wide">
                  {t.objective}
                </label>
                <select
                  name="objective"
                  value={formData.objective}
                  onChange={(e) => setFormData({ ...formData, objective: e.target.value })}
                  required
                  className="w-full px-4 py-3 bg-white border border-[#E7E1D9] text-[#0B0B0B] focus:outline-none focus:border-[#F97700] transition-colors cursor-pointer"
                >
                  {t.objectiveOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm text-[#2E2E2E] mb-2 tracking-wide">
                    {t.assetValue}
                  </label>
                  <input
                    type="text"
                    name="assetValue"
                    value={formData.assetValue}
                    onChange={(e) => setFormData({ ...formData, assetValue: e.target.value })}
                    required
                    className="w-full px-4 py-3 bg-white border border-[#E7E1D9] text-[#0B0B0B] focus:outline-none focus:border-[#F97700] transition-colors"
                  />
                </div>

                <div>
                  <label className="block text-sm text-[#2E2E2E] mb-2 tracking-wide">
                    {t.monthlyPayment}
                  </label>
                  <input
                    type="text"
                    name="monthlyPayment"
                    value={formData.monthlyPayment}
                    onChange={(e) => setFormData({ ...formData, monthlyPayment: e.target.value })}
                    required
                    className="w-full px-4 py-3 bg-white border border-[#E7E1D9] text-[#0B0B0B] focus:outline-none focus:border-[#F97700] transition-colors"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm text-[#2E2E2E] mb-2 tracking-wide">
                  {t.timeframe}
                </label>
                <input
                  type="text"
                  name="timeframe"
                  value={formData.timeframe}
                  onChange={(e) => setFormData({ ...formData, timeframe: e.target.value })}
                  required
                  className="w-full px-4 py-3 bg-white border border-[#E7E1D9] text-[#0B0B0B] focus:outline-none focus:border-[#F97700] transition-colors"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm text-[#2E2E2E] mb-2 tracking-wide">
                    {t.hasConsortium}
                  </label>
                  <select
                    name="hasConsortium"
                    value={formData.hasConsortium}
                    onChange={(e) => setFormData({ ...formData, hasConsortium: e.target.value })}
                    required
                    className="w-full px-4 py-3 bg-white border border-[#E7E1D9] text-[#0B0B0B] focus:outline-none focus:border-[#F97700] transition-colors cursor-pointer"
                  >
                    <option value="">{t.objectiveOptions[0].label}</option>
                    <option value={t.yes}>{t.yes}</option>
                    <option value={t.no}>{t.no}</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm text-[#2E2E2E] mb-2 tracking-wide">
                    {t.hasAwardedLetter}
                  </label>
                  <select
                    name="hasAwardedLetter"
                    value={formData.hasAwardedLetter}
                    onChange={(e) => setFormData({ ...formData, hasAwardedLetter: e.target.value })}
                    required
                    className="w-full px-4 py-3 bg-white border border-[#E7E1D9] text-[#0B0B0B] focus:outline-none focus:border-[#F97700] transition-colors cursor-pointer"
                  >
                    <option value="">{t.objectiveOptions[0].label}</option>
                    <option value={t.yes}>{t.yes}</option>
                    <option value={t.no}>{t.no}</option>
                  </select>
                </div>
              </div>

              <div className="pt-4">
                <p className="text-xs text-[#2E2E2E] leading-relaxed mb-6">
                  {t.lgpdText}
                </p>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full px-8 py-4 bg-[#F97700] text-white text-base font-semibold tracking-wide hover:bg-[#E56E00] transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  {isSubmitting ? '...' : t.submit}
                </button>

                {submitMessage && (
                  <p className="text-center text-sm text-[#2E2E2E] mt-4">
                    {submitMessage}
                  </p>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
}